import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./DateTime.module.scss";
import { ClickAwayListener, Popper } from "@mui/material";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import { combineClasses } from "@/lib/utils";

export interface DateTimeInputProps<T extends FieldValues = FieldValues> {
  control?: Control<T>;
  register?: UseFormRegister<T>;
  validations?: RegisterOptions<T, Path<T>>;
  name: Path<T>;
  placeholder?: string;
  min?: string;
  max?: string;
  [key: string]: unknown;
}

// Stored value is a local "YYYY-MM-DDTHH:mm" string (no timezone) — the
// same shape CreateGathering's old RSVP_CLOSE_OPTIONS values already used,
// and directly parseable by `new Date(value)`.
const WEEK_DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const YEARS_PER_PAGE = 12;
const YEAR_GRID_ACTIVE_OFFSET = 2;
const MONTH_NAME_FORMATTER = new Intl.DateTimeFormat("en-US", { month: "long" });
const DATE_DISPLAY_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});
const TIME_DISPLAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, "0"));
const PERIODS = ["AM", "PM"] as const;
type Period = (typeof PERIODS)[number];

const padValue = (value: number) => String(value).padStart(2, "0");

const formatDateKey = (date: Date) =>
  `${date.getFullYear()}-${padValue(date.getMonth() + 1)}-${padValue(date.getDate())}`;

interface ParsedDateTime {
  date: Date;
  hour: string;
  minute: string;
  period: Period;
}

const parseDateTimeValue = (value?: string): ParsedDateTime | null => {
  if (!value) return null;

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;

  const [, yearStr, monthStr, dayStr, hourStr, minuteStr] = match;
  const date = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
  if (Number.isNaN(date.getTime())) return null;

  const hour24 = Number(hourStr);
  const minute = Number(minuteStr);
  const nearestMinute = Math.round(minute / 5) * 5;

  return {
    date,
    hour: padValue(hour24 % 12 || 12),
    minute: padValue(nearestMinute === 60 ? 0 : nearestMinute),
    period: hour24 >= 12 ? "PM" : "AM",
  };
};

const buildDateTimeValue = (date: Date, hour: string, minute: string, period: Period) => {
  let hour24 = Number(hour) % 12;
  if (period === "PM") hour24 += 12;

  return `${formatDateKey(date)}T${padValue(hour24)}:${padValue(Number(minute))}`;
};

const formatDisplayValue = (value?: string, placeholder = "Select date & time") => {
  const parsed = parseDateTimeValue(value);
  if (!parsed) return placeholder;

  const timeDate = new Date(2000, 0, 1, Number(parsed.hour) % 12 || 0, Number(parsed.minute));
  if (parsed.period === "PM") timeDate.setHours(timeDate.getHours() + 12);

  return `${DATE_DISPLAY_FORMATTER.format(parsed.date)} · ${TIME_DISPLAY_FORMATTER.format(timeDate)}`;
};

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const clampValue = (value: number, minValue: number, maxValue: number) =>
  Math.min(Math.max(value, minValue), maxValue);

const isDateDisabled = (date: Date, min?: string, max?: string) => {
  const dateKey = formatDateKey(date);
  if (min && dateKey < min) return true;
  if (max && dateKey > max) return true;
  return false;
};

const getYearPageStart = (year: number, minYear: number, maxYear: number) => {
  const maxPageStart = Math.max(minYear, maxYear - YEARS_PER_PAGE + 1);
  return clampValue(year - YEAR_GRID_ACTIVE_OFFSET, minYear, maxPageStart);
};

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M8 2V5M16 2V5M3 9H21M5 4H19C20.1046 4 21 4.89543 21 6V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V6C3 4.89543 3.89543 4 5 4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface ControlledDateTimeFieldProps {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name: string;
  placeholder?: string | undefined;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean | undefined;
}

const ControlledDateTimeField = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  min,
  max,
  disabled,
}: ControlledDateTimeFieldProps) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const today = useMemo(() => new Date(), []);
  const parsed = useMemo(() => parseDateTimeValue(value), [value]);

  const [isOpen, setIsOpen] = useState(false);
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(parsed?.date ?? null);
  const [timeSelection, setTimeSelection] = useState<{ hour: string; minute: string; period: Period }>({
    hour: parsed?.hour ?? "06",
    minute: parsed?.minute ?? "00",
    period: parsed?.period ?? "PM",
  });
  const [displayMonth, setDisplayMonth] = useState<Date>(startOfMonth(parsed?.date ?? today));

  const minYear = Math.min(Number(min?.slice(0, 4)) || today.getFullYear(), today.getFullYear(), 1900);
  const maxYear = Math.max(Number(max?.slice(0, 4)) || today.getFullYear(), today.getFullYear(), 2100);
  const [yearPageStart, setYearPageStart] = useState(() =>
    getYearPageStart(displayMonth.getFullYear(), minYear, maxYear),
  );

  useEffect(() => {
    setSelectedDate(parsed?.date ?? null);
    setTimeSelection({
      hour: parsed?.hour ?? "06",
      minute: parsed?.minute ?? "00",
      period: parsed?.period ?? "PM",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = startOfMonth(displayMonth);
    const gridStart = new Date(firstDayOfMonth);
    gridStart.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const currentDate = new Date(gridStart);
      currentDate.setDate(gridStart.getDate() + index);
      return {
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === displayMonth.getMonth(),
        isDisabled: isDateDisabled(currentDate, min, max),
      };
    });
  }, [displayMonth, max, min]);

  const closePicker = () => {
    setIsOpen(false);
    setIsYearPickerOpen(false);
    onBlur?.();
  };

  const openPicker = () => {
    if (disabled) return;
    setDisplayMonth(startOfMonth(selectedDate ?? today));
    setYearPageStart(getYearPageStart((selectedDate ?? today).getFullYear(), minYear, maxYear));
    setIsYearPickerOpen(false);
    setIsOpen(true);
  };

  const togglePicker = () => (isOpen ? closePicker() : openPicker());

  const commit = (date: Date | null, time: typeof timeSelection) => {
    if (!date) return;
    onChange(buildDateTimeValue(date, time.hour, time.minute, time.period));
  };

  const handleDateSelect = (date: Date) => {
    if (isDateDisabled(date, min, max)) return;
    setSelectedDate(date);
    setDisplayMonth(startOfMonth(date));
    commit(date, timeSelection);
  };

  const handleTimeSelect = (key: "hour" | "minute" | "period", nextValue: string) => {
    const nextSelection = { ...timeSelection, [key]: nextValue } as typeof timeSelection;
    setTimeSelection(nextSelection);
    commit(selectedDate ?? today, nextSelection);
    if (!selectedDate) setSelectedDate(today);
  };

  const handleClear = () => {
    onChange("");
    setSelectedDate(null);
    closePicker();
  };

  const handleYearSelect = (nextYear: number) => {
    setDisplayMonth((current) => new Date(nextYear, current.getMonth(), 1));
    setYearPageStart(getYearPageStart(nextYear, minYear, maxYear));
    setIsYearPickerOpen(false);
  };

  const maxYearPageStart = Math.max(minYear, maxYear - YEARS_PER_PAGE + 1);
  const yearOptions = useMemo(
    () => Array.from({ length: YEARS_PER_PAGE }, (_, index) => yearPageStart + index).filter((year) => year <= maxYear),
    [maxYear, yearPageStart],
  );

  return (
    <div className={styles.pickerWrapper}>
      <button
        ref={triggerRef}
        id={name}
        type="button"
        className={combineClasses(styles.inputField, isOpen && styles.isOpen, !value && styles.placeholderState)}
        onClick={togglePicker}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        disabled={disabled}
      >
        <span className={combineClasses(styles.valueText, !value && styles.placeholderText)}>
          {formatDisplayValue(value, placeholder || "Select date & time")}
        </span>
        <span className={styles.icon}>
          <CalendarIcon />
        </span>
      </button>

      <Popper
        open={isOpen}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        className={styles.popper}
        modifiers={[
          { name: "flip", enabled: true, options: { fallbackPlacements: ["top-start", "bottom-end", "top-end"], padding: 8 } },
          { name: "preventOverflow", enabled: true, options: { altAxis: true, tether: true, padding: 8 } },
        ]}
      >
        <ClickAwayListener onClickAway={closePicker}>
          <div className={styles.popup} role="dialog" aria-label="Choose date and time">
            <div className={styles.header}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1))}
                aria-label="Previous month"
              >
                <ChevronLeftIcon />
              </button>

              <div className={styles.headerTitle}>
                <button
                  type="button"
                  className={combineClasses(styles.headerValueButton, isYearPickerOpen && styles.headerValueButtonActive)}
                  onClick={() => setIsYearPickerOpen((open) => !open)}
                >
                  {displayMonth.getFullYear()}
                </button>
                <div className={styles.monthLabel}>{MONTH_NAME_FORMATTER.format(displayMonth)}</div>
              </div>

              <button
                type="button"
                className={styles.navButton}
                onClick={() => setDisplayMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1))}
                aria-label="Next month"
              >
                <ChevronRightIcon />
              </button>
            </div>

            <div className={styles.bodyDivider} />

            {isYearPickerOpen ? (
              <div className={styles.yearPickerPanel}>
                <div className={styles.yearPager}>
                  <button
                    type="button"
                    className={styles.yearPagerButton}
                    onClick={() => setYearPageStart((start) => Math.max(minYear, start - YEARS_PER_PAGE))}
                    disabled={yearPageStart <= minYear}
                    aria-label="Previous years"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <div className={styles.yearRangeLabel}>
                    {yearPageStart} - {Math.min(yearPageStart + YEARS_PER_PAGE - 1, maxYear)}
                  </div>
                  <button
                    type="button"
                    className={styles.yearPagerButton}
                    onClick={() => setYearPageStart((start) => Math.min(maxYearPageStart, start + YEARS_PER_PAGE))}
                    disabled={yearPageStart >= maxYearPageStart}
                    aria-label="Next years"
                  >
                    <ChevronRightIcon />
                  </button>
                </div>

                <div className={styles.yearGrid}>
                  {yearOptions.map((year) => (
                    <button
                      key={year}
                      type="button"
                      className={combineClasses(styles.yearButton, displayMonth.getFullYear() === year && styles.selectedYearButton)}
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className={styles.weekdays}>
                  {WEEK_DAYS.map((day, index) => (
                    <span key={`${day}-${index}`} className={styles.weekday}>
                      {day}
                    </span>
                  ))}
                </div>

                <div className={styles.daysGrid}>
                  {calendarDays.map(({ date, isCurrentMonth, isDisabled: disabledDay }) => {
                    const isSelected = Boolean(selectedDate) && formatDateKey(selectedDate as Date) === formatDateKey(date);
                    const isToday = formatDateKey(date) === formatDateKey(today);

                    return (
                      <button
                        key={`${date.toISOString()}-${isCurrentMonth}`}
                        type="button"
                        className={combineClasses(
                          styles.dayButton,
                          !isCurrentMonth && styles.outsideMonth,
                          isToday && styles.todayButton,
                          isSelected && styles.selectedDay,
                          disabledDay && styles.disabledDay,
                        )}
                        onClick={() => handleDateSelect(date)}
                        disabled={disabledDay}
                        aria-pressed={Boolean(isSelected)}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className={styles.bodyDivider} />

                <div className={styles.timeColumns}>
                  <div className={styles.timeColumn}>
                    <span className={styles.columnLabel}>Hour</span>
                    <div className={styles.optionList}>
                      {HOURS.map((hour) => (
                        <button
                          key={hour}
                          type="button"
                          className={combineClasses(styles.timeOption, timeSelection.hour === hour && styles.selectedOption)}
                          onClick={() => handleTimeSelect("hour", hour)}
                        >
                          {hour}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.timeColumn}>
                    <span className={styles.columnLabel}>Min</span>
                    <div className={styles.optionList}>
                      {MINUTES.map((minute) => (
                        <button
                          key={minute}
                          type="button"
                          className={combineClasses(styles.timeOption, timeSelection.minute === minute && styles.selectedOption)}
                          onClick={() => handleTimeSelect("minute", minute)}
                        >
                          {minute}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.timeColumn}>
                    <span className={styles.columnLabel}>&nbsp;</span>
                    <div className={styles.optionList}>
                      {PERIODS.map((period) => (
                        <button
                          key={period}
                          type="button"
                          className={combineClasses(styles.timeOption, timeSelection.period === period && styles.selectedOption)}
                          onClick={() => handleTimeSelect("period", period)}
                        >
                          {period}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {!isYearPickerOpen && (
              <div className={styles.footer}>
                <button type="button" className={styles.footerButton} onClick={handleClear}>
                  Clear
                </button>
                <button
                  type="button"
                  className={combineClasses(styles.footerButton, styles.footerButtonPrimary)}
                  onClick={closePicker}
                  disabled={!selectedDate}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

const DateTimeInput = <T extends FieldValues = FieldValues>({
  control,
  register,
  validations,
  name,
  placeholder,
  min,
  max,
  ...rest
}: DateTimeInputProps<T>) => {
  const renderNativeInput = (field: Record<string, unknown> = {}) => (
    <input {...field} {...rest} id={name} type="datetime-local" className={styles.nativeInput} placeholder={placeholder} />
  );

  if (control) {
    return (
      <Controller
        control={control}
        name={name}
        {...(validations ? { rules: validations } : {})}
        render={({ field }) => (
          <ControlledDateTimeField
            {...rest}
            name={name}
            placeholder={placeholder}
            min={min}
            max={max}
            value={(field.value as string) || ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    );
  }

  if (register) {
    const field = register(name, validations);
    return renderNativeInput(field);
  }

  return renderNativeInput();
};

const DateTimeInputWithLabelAndError = withLabelAndError(DateTimeInput);

export default DateTimeInputWithLabelAndError;
export { DateTimeInput };
